/**
 * How a RAG value renders, in one place.
 *
 * Round 12 B1 -- the headline gained a fourth state, `not_assessed`, and
 * the thing that made the original defect possible was every surface
 * mapping the colour for itself: a value nobody had thought about fell
 * through to green, and green is a claim. Four surfaces rendered the
 * absence of evidence as assurance on a bank that had assessed nothing.
 *
 * So the mapping is shared, and `not_assessed` is NEUTRAL -- not a
 * paler green, not an amber warning. It is not a verdict on the estate
 * at all; it says nobody has been able to reach one yet.
 */
import type { RagTone } from "shared/components/Card";
import type { PillTone } from "shared/components/StatusPill";
import type { Rag } from "shared/api/types";

export const RAG_NOT_ASSESSED = "not_assessed";

/** True when there is nothing to judge, so no colour is honest. */
export function isAssessedRag(rag: string | null | undefined): boolean {
  return rag !== RAG_NOT_ASSESSED;
}

/** The pill tone for a RAG value. Neutral where nothing was judged. */
export function ragPillTone(rag: string | null | undefined): PillTone {
  return rag === "red"
    ? "red"
    : rag === "amber"
      ? "amber"
      : rag === "green"
        ? "green"
        : "neutral";
}

/**
 * The card's status edge, which carries a RAG and nothing else.
 *
 * `null` where nothing was judged: an edge in any colour would be the
 * claim this exists to stop.
 */
export function ragCardStatus(rag: string | null | undefined): RagTone | null {
  return rag === "red" || rag === "amber" || rag === "green" ? rag : null;
}

/** The catalogue key for the word beside the pill. */
export function ragWordKey(rag: Rag | string): string {
  return isAssessedRag(rag) ? String(rag) : RAG_NOT_ASSESSED;
}
